import {FC, useEffect, useMemo} from 'react'
import {useMutationState} from '@tanstack/react-query'
import {MUTATION_KEYS} from '@/queryClient'
import {ObjectKeys} from '@/utilities/helpers'
import {PatientEditingFieldsGroup, usePatientDetailsStore} from '@/features/patients/store/usePatientDetailsStore'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {HttpUpdatePatientOptions} from '@/features/patients/services/patients.http'

interface PatientDetailsEditButtonsProps {
    groupName: PatientEditingFieldsGroup
    isConfirmDisabled: boolean
    onCancel: () => void
    onConfirm: () => void
}

export const PatientDetailsEditButtons: FC<PatientDetailsEditButtonsProps> = ({
    groupName,
    isConfirmDisabled,
    onCancel,
    onConfirm
}) => {
    const currentEditingFieldsGroup = usePatientDetailsStore(state => state.editingFieldsGroup)
    const setEditingFieldsGroup = usePatientDetailsStore(state => state.setEditingFieldsGroup)

    // retrieve pending state from mutation
    const updatePatientState = useMutationState({
        filters: {mutationKey: [MUTATION_KEYS.UPDATE_PATIENT], status: 'pending'},
        select: mutation => ({
            variables: mutation.state.variables as HttpUpdatePatientOptions
        })
    })

    // constants
    const isLoadingUpdate = useMemo(() => !!ObjectKeys(updatePatientState).length, [updatePatientState])
    const isActive = useMemo(() => groupName == currentEditingFieldsGroup, [groupName, currentEditingFieldsGroup])

    // clear store on unmount
    useEffect(() => {
        return () => setEditingFieldsGroup(null)
    }, [])

    return (
        <EditDetailsSectionButtons
            isActive={isActive}
            isLoading={isLoadingUpdate}
            isDisabled={isConfirmDisabled && isActive}
            onEdit={() => setEditingFieldsGroup(groupName)}
            onConfirm={onConfirm}
            onCancel={() => {
                setEditingFieldsGroup(null)
                onCancel()
            }}
        />
    )
}
